import { Component } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { BlacklistItem } from '@mol-limo-reg/admin-api';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { DeleteBlacklistItemDialogComponent } from '../dialogs/delete-blacklist-item-dialog/delete-blacklist-item-dialog.component';
import { AddBlacklistItemDialogComponent } from '../dialogs/add-blacklist-item-dialog/add-blacklist-item-dialog.component';
import { normalizePhoneNumber, phoneNumberRegex } from '../../core/validation/phone-validation';
import { atLeastOneFormFieldRequired } from '../../core/validation/form-validation';
import { MatTableDataSource } from '@angular/material/table';
import { SnackbarService } from '../../services/snackbar.service';
import { BlacklistService } from '../../services/blacklist.service';
import { DATE_ISO_FORMAT, DATE_SLASH_FORMAT } from '../../core/formatting/date-formatting';
import * as moment from 'moment';
import { catchError, of } from 'rxjs';
import { normalizeEmail } from '../../core/validation/email-validation';
import { normalizeLicenseId } from '../../core/validation/license-validation';
import { addressCardValidator } from 'src/app/core/validation/address-card.validator';

@Component({
  selector: 'app-blacklist',
  templateUrl: './blacklist.component.html',
  styleUrls: ['./blacklist.component.scss'],
})
export class BlacklistComponent {
  queryFormGroup = new UntypedFormGroup(
    {
      email: new UntypedFormControl('', [Validators.email]),
      phoneNumber: new UntypedFormControl('', [Validators.pattern(phoneNumberRegex)]),
      licenseNumber: new UntypedFormControl('', []),
      birthDate: new UntypedFormControl('', []),
      addressCardNumber: new UntypedFormControl('', [addressCardValidator]),
    },
    [atLeastOneFormFieldRequired]
  );

  displayedColumns = ['reason', 'createdAt', 'createdByName', 'options'];
  dataSource = new MatTableDataSource<BlacklistItem>([]);

  loading = false;
  searchWasPerformed = false;
  birthDateMax = moment();

  constructor(private dialog: MatDialog, private snackbarService: SnackbarService, private blacklistService: BlacklistService) {}

  onSubmitQuery() {
    if (this.queryFormGroup.valid) {
      let email = this.undefinedIfEmpty(normalizeEmail(this.queryFormGroup.get('email')?.value));
      let phoneNumber = this.undefinedIfEmpty(normalizePhoneNumber(this.queryFormGroup.get('phoneNumber')?.value));
      let licenseNumber = this.undefinedIfEmpty(normalizeLicenseId(this.queryFormGroup.get('licenseNumber')?.value));
      let birthDate = this.undefinedIfEmpty(this.queryFormGroup.get('birthDate')?.value)?.format(DATE_ISO_FORMAT);
      let addressCardNumber = this.undefinedIfEmpty(this.queryFormGroup.get('addressCardNumber')?.value)?.trim?.()?.toUpperCase?.();

      this.loading = true;
      this.blacklistService
        .searchBlacklistItems$(email, phoneNumber, licenseNumber, birthDate, addressCardNumber)
        .pipe(
          catchError(() => {
            this.loading = false;
            this.snackbarService.error(`A feketelista lekérése során hiba lépett fel!`);
            return of();
          })
        )
        .subscribe((blacklistItems) => {
          this.dataSource.data = blacklistItems;
          this.searchWasPerformed = true;
          this.loading = false;
        });
    } else if (this.queryFormGroup.get('addressCardNumber')!.hasError('addressCard')) {
      this.snackbarService.error('A lakcímkártya formátum helytelen. Kérlek próbáld meg újra!');
    } else if (this.queryFormGroup.hasError('atLeastOneRequired')) {
      this.snackbarService.error('A keresés indításához legalább egy mezőt tölts ki!');
      this.markQueryIncorrect();
    }
  }

  markQueryIncorrect() {
    this.queryFormGroup.get('email')?.setErrors({ incorrect: true });
    this.queryFormGroup.get('phoneNumber')?.setErrors({ incorrect: true });
    this.queryFormGroup.get('licenseNumber')?.setErrors({ incorrect: true });
    this.queryFormGroup.get('birthDate')?.setErrors({ incorrect: true });
    this.queryFormGroup.get('addressCardNumber')?.setErrors({ incorrect: true });
  }

  clearQueryIncorrect() {
    this.queryFormGroup.get('email')?.setErrors({ incorrect: true });
    this.queryFormGroup.get('email')?.updateValueAndValidity();
    this.queryFormGroup.get('phoneNumber')?.setErrors({ incorrect: true });
    this.queryFormGroup.get('phoneNumber')?.updateValueAndValidity();
    this.queryFormGroup.get('licenseNumber')?.setErrors({ incorrect: true });
    this.queryFormGroup.get('licenseNumber')?.updateValueAndValidity();
    this.queryFormGroup.get('birthDate')?.setErrors({ incorrect: true });
    this.queryFormGroup.get('birthDate')?.updateValueAndValidity();
    this.queryFormGroup.get('addressCardNumber')?.setErrors({ incorrect: true });
    this.queryFormGroup.get('addressCardNumber')?.updateValueAndValidity();
  }

  openCreateItemDialog() {
    this.dialog.open(AddBlacklistItemDialogComponent, {
      autoFocus: 'dialog',
      width: '720px',
    });
  }

  openDeleteItemDialogFor(item: BlacklistItem) {
    let dialogRef = this.dialog.open(DeleteBlacklistItemDialogComponent, {
      data: {
        id: item.id,
        reason: item.reason,
        date: moment(item.createdAt),
      },
      autoFocus: 'dialog',
      width: '720px',
    });
    this.removeItemAfterSuccessfulDialog(dialogRef, item);
  }

  dataAvailable() {
    return !(this.dataSource.data.length === 0) && !this.loading;
  }

  formatDate(date: string) {
    return moment(date).format(DATE_SLASH_FORMAT);
  }

  undefinedIfEmpty(input: any) {
    return input === '' || input === null ? undefined : input;
  }

  removeItemAfterSuccessfulDialog(dialogRef: MatDialogRef<any>, toRemove: BlacklistItem) {
    dialogRef.afterClosed().subscribe((result) => {
      if (result.success) {
        this.dataSource.data = this.dataSource.data.filter((item) => item !== toRemove);
      }
    });
  }
}
